import {tx, ptx, usePageText} from '../lib/page-text';
import { useState, useEffect, useRef, useId } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { PlusIcon } from '../components/icons';

interface Target {
  id: string;
  name: string;
  platform: string;
  rtmpUrl: string;     // masked by the API
  active: boolean;
  createdAt: string;
}

const PLATFORM_LABEL: Record<string, string> = {
  youtube: 'YouTube Live',
  twitch:  'Twitch',
  facebook: 'Facebook Live',
  x: 'X / Twitter Live',
  linkedin: 'LinkedIn Live',
  custom: 'Custom RTMP',
};
const PLATFORM_COLOR: Record<string, string> = {
  youtube: '#ff0000',
  twitch: '#9146ff',
  facebook: '#1877f2',
  x: '#000',
  linkedin: '#0a66c2',
  custom: '#6e7178',
};

export function RestreamPage() {
  usePageText();
  const [showAdd, setShowAdd] = useState(false);

  const list = useQuery({
    queryKey: ['restream-targets'],
    queryFn: async () => (await api.get('/me/restream/targets')).data as Target[],
    refetchInterval: 30_000,
  });

  return (
    <div>
      <div className="row-between" style={{ marginBottom: 8 }}>
        <div>
          <h1 className="h1">{tx("Restream destinations")}</h1>
          <p className="subtitle">{tx("Forward your live streams to YouTube, Twitch, Facebook, X, LinkedIn, or any custom RTMP endpoint. Save your channels here, then attach them in Live Stream → Studio → Destinations.")}</p>
        </div>
        <button className="btn" onClick={() => setShowAdd(true)}>{ptx("{v0} Add destination", {v0: (<PlusIcon size={14}/>)})}</button>
      </div>

      {list.isLoading && <div className="muted">{tx("Loading…")}</div>}
      {list.isError && <div className="error">{tx("Failed to load destinations.")}</div>}

      {list.data && list.data.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <h3 style={{ marginBottom: 8 }}>{tx("No destinations yet")}</h3>
          <p className="muted" style={{ marginBottom: 20, maxWidth: 460, marginLeft: 'auto', marginRight: 'auto' }}>{tx("Add a YouTube, Twitch, Facebook, X, or LinkedIn destination to simulcast your live streams. Attach a destination inside your stream workspace to forward its broadcast video and audio.")}</p>
          <button className="btn" onClick={() => setShowAdd(true)}>{tx("Add your first destination")}</button>
        </div>
      )}

      {list.data && list.data.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))', gap: 14 }}>
          {list.data.map((t) => <TargetCard key={t.id} target={t}/>)}
        </div>
      )}

      <div className="card" style={{ marginTop: 24 }}>
        <h3 style={{ marginBottom: 8 }}>{tx("How attaching works")}</h3>
        <p className="muted" style={{ fontSize: 13, lineHeight: 1.6 }}>{tx("Open Live Stream, choose your stream, then Studio → Destinations. Attach a saved channel, check its connection status, and retry or detach without leaving your broadcast. Audience permissions on each external platform are managed separately.")}</p>
      </div>

      {showAdd && <AddTargetModal onClose={() => setShowAdd(false)} />}
    </div>
  );
}

function TargetCard({ target }: { target: Target }) {
  usePageText();
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: async () => (await api.delete(`/me/restream/targets/${target.id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['restream-targets'] }),
  });
  const color = PLATFORM_COLOR[target.platform] ?? '#6e7178';
  return (
    <div className="card hover" style={{ padding: 18 }}>
      <div className="row-between" style={{ marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{target.name}</div>
          <div style={{ fontSize: 12, color, marginTop: 2 }}>
            {PLATFORM_LABEL[target.platform] ?? target.platform}
          </div>
        </div>
        <span className="badge" style={{ fontSize: 10 }}>
          {target.active ? tx("Active") : tx("Disabled")}
        </span>
      </div>
      <div className="code" style={{ fontSize: 11, padding: '8px 10px', marginBottom: 12 }}>
        <span className="val">{target.rtmpUrl}</span>
      </div>
      <div className="muted" style={{ fontSize: 11, marginBottom: 12 }}>{tx("Created ")}{new Date(target.createdAt).toLocaleDateString()}
      </div>
      {del.isError && <p role="alert" className="error">{tx("Could not delete this destination. Please retry.")}</p>}
      <div className="flex gap">
        <button
          className="btn ghost sm"
          style={{ color: 'var(--danger)' }}
          onClick={() => { if (confirm('Delete this destination? Active forwarders will stop.')) del.mutate(); }}
          disabled={del.isPending}
        >
          {del.isPending ? tx("Deleting…") : tx("Delete")}
        </button>
      </div>
    </div>
  );
}

export function AddTargetModal({ onClose }: { onClose: () => void }) {
  usePageText();
  const dialog = useRef<HTMLDialogElement>(null), id = useId();
  useEffect(() => { const previous = document.activeElement as HTMLElement | null; dialog.current?.showModal(); return () => { if (previous?.isConnected) previous.focus(); }; }, []);
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [platform, setPlatform] = useState<keyof typeof PLATFORM_LABEL>('youtube');
  const [rtmpUrl, setRtmpUrl] = useState('');
  const [err, setErr] = useState('');

  const create = useMutation({
    mutationFn: async () => (await api.post('/me/restream/targets', { name, platform, rtmpUrl })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['restream-targets'] }); onClose(); },
    onError: (e: any) => setErr(e?.response?.data?.message || e?.message || 'Failed'),
  });

  return (
    <dialog ref={dialog} className="modal destination-dialog" aria-labelledby={id+"-title"} onCancel={onClose} style={{width:"min(520px, calc(100vw - 32px))",maxHeight:"calc(100dvh - 32px)",overflow:"auto",margin:"auto",border:"1px solid var(--border)",borderRadius:16}}>
      <h2 id={id+"-title"}>{tx("Add restream destination")}</h2>
      <p className="modal-subtitle">{ptx("Each destination is one RTMP URL. Get the URL + stream-key from the platform's broadcast settings, then combine them into a single {v0} URL.", {v0: (<code>rtmp://server/path/key</code>)})}</p>

      <label className="label" htmlFor={id+"-name"}>{tx("Friendly name")}</label>
      <input id={id+"-name"} className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)}
             placeholder={tx("e.g. Brand YouTube channel")}/>

      <label className="label" htmlFor={id+"-platform"} style={{ marginTop: 14 }}>{tx("Platform")}</label>
      <select id={id+"-platform"} className="input" value={platform} onChange={(e) => setPlatform(e.target.value as any)}>
        {Object.entries(PLATFORM_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>

      <label className="label" htmlFor={id+"-url"} style={{ marginTop: 14 }}>{tx("RTMP URL (server + stream key)")}</label>
      <input id={id+"-url"} type="password" autoComplete="off" aria-label={tx("RTMP URL (server + stream key)")} className="input mono" value={rtmpUrl} onChange={(e) => setRtmpUrl(e.target.value)}
             placeholder={"rtmp://a.rtmp.youtube.com/live2/abcd-efgh-ijkl-mnop"}/>
      <div className="muted" style={{ fontSize: 11, marginTop: 6, lineHeight: 1.5 }}>{tx("Full URL including the stream key. Saved publishing paths are hidden in destination lists.")}{' '}
        <a href="https://kardocloud.com/Live/Developer/restream.html#platforms" target="_blank" rel="noopener" style={{ color: 'var(--primary)' }}>{tx("Per-platform setup guide ↗")}</a>
      </div>

      {err && <div className="error" style={{ marginTop: 10 }}>{err}</div>}

      <div className="actions">
        <button className="btn outline" onClick={onClose}>{tx("Cancel")}</button>
        <button className="btn" disabled={!name || !rtmpUrl || create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? tx("Saving…") : tx("Save destination")}
        </button>
      </div>
    </dialog>
  );
}
